import { Component, Input, ElementRef } from '@angular/core';
import {
  SpotLadderResult,
  CurrencyResult
} from '@approot/models/spot-ladder.js';

import * as d3 from 'd3-selection';
import * as d3Scale from 'd3-scale';
import * as d3ScaleChromatic from 'd3-scale-chromatic';
import * as d3Shape from 'd3-shape';
import * as d3Array from 'd3-array';
import * as d3Axis from 'd3-axis';
import { Utilities, DateUtilities } from '@approot/utilities';
@Component({
  selector: 'app-spot-ladder-graph',
  templateUrl: './spot-ladder-graph.component.html',
  styleUrls: ['./spot-ladder-graph.component.scss']
})
export class SpotLadderGraphComponent {
  data: SpotLadderResult[];
  xData: number[];
  currentData: any;

  @Input() availableDates: string[];

  private _dates: string[];
  @Input() spotLadderResults: SpotLadderResult[];
  @Input() set dates(value: string[]) {
    this._dates = value;
    this.redrawGraph();
  }
  get dates(): string[] {
    return this._dates;
  }

  private _legs: string[];
  @Input() set legs(value: string[]) {
    this._legs = value;
    this.redrawGraph();
  }
  get legs(): string[] {
    return this._legs;
  }

  private _reportingCurrency: string;
  @Input() set reportingCurrency(value: string) {
    this._reportingCurrency = value;
    this.redrawGraph();
  }
  get reportingCurrency(): string {
    return this._reportingCurrency;
  }

  private _outputData: string;
  @Input() set outputData(value: string) {
    this._outputData = value;
    this.redrawGraph();
  }
  get outputData(): string {
    return this._outputData;
  }

  svg: any;
  margin = { top: 20, right: 80, bottom: 30, left: 50 };
  g: any;
  width: number;
  height: number;
  x;
  y;
  z;
  line;
  colorMatcher;
  reducedData: any[] = [];

  dateColorMatch = [];

  constructor(private elem: ElementRef) {}

  redrawGraph() {
    this.drawGraph();
  }

  drawGraph() {
    d3.select('svg#spot-ladder').html('');
    this.reducedData = [];
    this.dateColorMatch = [];

    if (
      this.dates &&
      this.dates.length &&
      this.legs &&
      this.legs.length &&
      this.outputData &&
      this.reportingCurrency &&
      this.spotLadderResults
    ) {
      this.xData = this.spotLadderResults.filter(
        y => y[this.outputData]
      )[0].spots;

      let dateFiltered = this.spotLadderResults.filter(
        (x: SpotLadderResult) => {
          return this.dates.includes(x.date);
        }
      );

      dateFiltered.forEach((y: SpotLadderResult) => {
        this.reducedData.push({
          id: y.date,
          values: this.calculateGraphValues(y)
        });
      });

      this.initChart();
      this.drawAxis();
      this.drawPath();
    }
  }

  sumArrays(arrays) {
    const n = arrays.reduce((max, xs) => Math.max(max, xs.length), 0);
    const result = Array.from({ length: n });
    return result.map((_, i) =>
      arrays.map(xs => xs[i] || 0).reduce((sum, x) => sum + x, 0)
    );
  }

  calculateGraphValues(y: SpotLadderResult): any[] {
    let spots = y.spots;
    let currencyResults: CurrencyResult[] =
      y[this.outputData][this.reportingCurrency];

    let b: number[][] = [];
    currencyResults.forEach((r: CurrencyResult) => {
      if (this.legs.includes(r.name)) {
        b.push(r.data);
      }
    });

    let sums = this.sumArrays(b);
    let results = [];
    for (let i = 0; i < sums.length; i++) {
      results.push({
        spot: spots[i],
        val: sums[i]
      });
    }
    return results;
  }

  initChart() {
    // let container = d3.select('.chart-container');
    let container = this.elem.nativeElement.querySelector('.chart-container');

    let containerWidth = container.offsetWidth;
    let containerHeight = container.offsetHeight;

    this.svg = d3.select('svg#spot-ladder');

    this.svg
      .attr('width', containerWidth * 0.9)
      .attr('height', containerHeight);

    this.width = this.svg.attr('width') - this.margin.left - this.margin.right;
    this.height =
      this.svg.attr('height') - this.margin.top - this.margin.bottom;

    this.g = this.svg
      .append('g')
      .attr(
        'transform',
        'translate(' + this.margin.left + ',' + this.margin.top + ')'
      );

    let colorSwatch = [
      '#1675B6',
      '#FF7F00',
      '#22A121',
      '#D9231E',
      '#9464BF',
      '#8D5549',
      '#E575C3',
      '#7F7F7F',
      '#BDBF00',
      '#00BED1'
    ];
    for (let i = 0; i < this.availableDates.length; i++) {
      this.dateColorMatch.push({
        date: this.availableDates[i],
        color: colorSwatch[i]
      });
    }

    //this.x = d3Scale.scaleTime().range([0, this.width]);
    this.x = d3Scale.scaleLinear().range([0, this.width]);
    this.y = d3Scale.scaleLinear().range([this.height, 0]);
    this.z = d3Scale.scaleOrdinal(d3ScaleChromatic.schemeCategory10);
    // this.colorMatcher = date => {
    //   return dateColorMatch.map(x => {
    //     if (x.date == date) {
    //       return x.color;
    //     }
    //   });
    // };

    this.line = d3Shape
      .line()
      .curve(d3Shape.curveBasis)
      .x((d: any) => {
        return this.x(d.spot);
      })
      .y((d: any) => {
        return this.y(d.val);
      });

    this.x.domain(d3Array.extent(this.xData, (d: number) => d));

    this.y.domain([
      d3Array.min(this.reducedData, function (c) {
        return d3Array.min(c.values, function (d) {
          return d.val;
        });
      }),
      d3Array.max(this.reducedData, function (c) {
        return d3Array.max(c.values, function (d) {
          return d.val;
        });
      })
    ]);

    this.z.domain(
      this.reducedData.map(function (c) {
        return c.id;
      })
    );
  }

  private drawAxis(): void {
    this.g
      .append('g')
      .attr('class', 'axis axis--x')
      .attr('transform', 'translate(0,' + this.height + ')')
      .call(d3Axis.axisBottom(this.x));

    this.g
      .append('g')
      .attr('class', 'axis axis--y')
      .call(
        d3Axis.axisLeft(this.y).tickFormat(function (v) {
          return Utilities.abbreviateNumber(v);
        })
      )
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', 6)
      .attr('dy', '0.71em')
      .attr('fill', 'none');
  }

  private drawPath(): void {
    const colors = this.dateColorMatch; // the colors are stored here as datecolorMatch is unavailable in the mouse event function
    let spotCurve = this.g
      .selectAll('.spotCurve')
      .data(this.reducedData)
      .enter()
      .append('g')
      .attr('class', 'spotCurve');

    spotCurve
      .append('path')
      .attr('class', 'line')
      .attr('d', (d: any) => {
        return this.line(d.values);
      })
      .attr('fill', 'none')
      .style('stroke', d => {
        let result = this.dateColorMatch.filter(x => {
          return x.date === d.id;
        });
        return result[0].color;
      });

    // we are drawing two curves that are the same,
    // where one of the curves are invisible so that we don't have to hover directly over the line to see the tooltip
    spotCurve
      .append('path')
      .attr('class', 'hidden-line')
      .attr('d', (d: any) => {
        return this.line(d.values);
      })
      .attr('fill', 'none')
      .style('stroke', 'transparent')
      .style('stroke-width', '10px');

    let tooltip;

    // this is the tool tip.
    // the tooltip is added to the body as, this is the only way that the it can be correctly mapped
    // to a position relative to the mouse. And this is how its recommended to be done

    if (!d3.select('.spot-ladder-graph-tooltip')._groups[0][0]) {
      // if the tooltip div already exists don't make another one just re use it,
      // else make a new one
      tooltip = d3
        .select('body')
        .append('div')
        .attr('class', 'spot-ladder-graph-tooltip')
        .attr('role', 'tooltip');
    } else {
      tooltip = d3.select('.spot-ladder-graph-tooltip');
    }
    spotCurve.on('mouseover', function (d) {
      // when the mouse hits the line

      const color = colors.filter(x => {
        return x.date === d.id;
      })[0].color;

      tooltip
        .html(DateUtilities.dateToPrettyDate(d.id)) // the date needs to be set in the tooltip every time the lines are being hovered
        .style('visibility', 'visible')
        .style('left', d3.event.pageX + -35 + 'px')
        .style('top', d3.event.pageY + -35 + 'px')
        .style('background-color', color)
        .style('border-top-color', color);
    });

    // d3.event.page y/x is used as the coordinate as too where on the lines is being hovered
    spotCurve.on('mousemove', function (d) {
      // as the mouse is moving along the the line
      tooltip
        .style('visibility', 'visible')
        .style('left', d3.event.pageX + -35 + 'px')
        .style('top', d3.event.pageY + -35 + 'px');
    });

    spotCurve.on('mouseout', function (d) {
      // as the mouse leaves the line
      tooltip.style('visibility', 'hidden');
    });
  }
}
